import React, { useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, IconButton, Collapse, Chip, useMediaQuery, useTheme, Card, CardContent, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@material-ui/core';
import { Assignment, ExpandMore, ExpandLess } from '@material-ui/icons';
import useStyles from '../styles/PremiumBacklink.style';
import usePremiumBacklink from '../hooks/usePremiumBacklink';

const OrdersInProcessTable = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    orders, isFetchingOrders, getStatusStyle, getStatusLabel, requiresLinkDetails,
  } = usePremiumBacklink();
  const [expandedRows, setExpandedRows] = useState(new Set());

  const hasOrders = orders && orders.length > 0;

  const handleRowExpand = (orderId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(orderId)) {
      newExpandedRows.delete(orderId);
    } else {
      newExpandedRows.add(orderId);
    }
    setExpandedRows(newExpandedRows);
  };


  const renderMobileOrderCard = (order) => (
      <Card key={order.id} style={{ marginBottom: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <CardContent style={{ padding: '16px' }}>
          {/* Order Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginBottom: '4px', wordBreak: 'break-all' }}>
                {(() => {
                  const domains = Array.from(new Set(
                    (order.order_items || [])
                      .map((item) => item.backlink_object?.webmaster_domain)
                      .filter(Boolean),
                  ));
                  return domains.length > 0 ? domains.join(', ') : 'No domain';
                })()}
              </Typography>
              <Typography variant="body2" style={{ color: '#666', fontSize: '12px' }}>
                {order.added ? new Date(order.added).toLocaleDateString() : 'No date'}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="h6" style={{ fontSize: '18px', fontWeight: 700, color: '#1a3552', marginBottom: '4px' }}>
                ${order.total_cost ? order.total_cost.toLocaleString() : '0'}
              </Typography>
              {order.status && (
                <Chip
                  label={getStatusLabel(order.status)}
                  size="small"
                  style={{
                    ...getStatusStyle(order.status),
                    fontWeight: 500,
                    fontSize: '10px',
                    height: '18px'
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Order Summary */}
          <Box mb={2}>
            <Typography variant="body2" style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>
              Order Summary
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500, color: '#333', marginBottom: '2px' }}>
              {order.regular_backlinks?.length || 0} backlinks from {order.order_items?.length || 0} publications
            </Typography>
            <Typography variant="body2" style={{ color: '#666', fontSize: '11px' }}>
              {order.order_items?.map(item => item.backlink_object?.name).filter(Boolean).join(', ') || 'No backlinks'}
            </Typography>
          </Box>

          {requiresLinkDetails && order.order_items?.some((it) => it.target_url || it.desired_anchor) && (
            <Box mb={2} p={1.5} style={{ background: '#F8FBFD', border: '1px solid #E4E7EB', borderRadius: 8 }}>
              <Typography
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: '#0E7BAF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  marginBottom: 8,
                }}
              >
                Requested Links
              </Typography>
              {order.order_items.filter((it) => it.target_url || it.desired_anchor).map((it, idx) => (
                <Box
                  key={it.id || `oi-${idx}`}
                  pt={idx > 0 ? 1 : 0}
                  mt={idx > 0 ? 1 : 0}
                  style={idx > 0 ? { borderTop: '1px dashed #DDE5EE' } : {}}
                >
                  <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#1A2332' }}>
                    {it.backlink_object?.name || it.backlink_object?.webmaster_domain || '-'}
                  </Typography>
                  <Typography style={{ fontSize: '11px', color: '#6C7480', marginTop: 2, wordBreak: 'break-all' }}>
                    Target: <strong style={{ color: '#1A2332' }}>{it.target_url || '-'}</strong>
                  </Typography>
                  <Typography style={{ fontSize: '11px', color: '#6C7480', marginTop: 2 }}>
                    Anchor: <strong style={{ color: '#1A2332' }}>{it.desired_anchor || '-'}</strong>
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Expandable Backlinks */}
          {order.regular_backlinks && order.regular_backlinks.length > 0 && (
            <Accordion style={{ boxShadow: 'none', margin: 0 }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                style={{ padding: '0', minHeight: 'auto' }}
              >
                <Typography variant="body2" style={{ fontSize: '12px', color: '#666' }}>
                  View {order.regular_backlinks.length} backlink{order.regular_backlinks.length !== 1 ? 's' : ''}
                </Typography>
              </AccordionSummary>
              <AccordionDetails style={{ padding: '8px 0' }}>
                <Box width="100%">
                  {order.regular_backlinks.map((backlink, index) => (
                    <Card key={backlink.id || `backlink-${index}`} style={{ marginBottom: '8px', backgroundColor: '#f9f9f9' }}>
                      <CardContent style={{ padding: '12px' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box flex={1}>
                            <Typography variant="body2" style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>
                              {backlink.webmaster_domain || '-'}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#666', fontSize: '11px' }}>
                              DR: {backlink.dr || '-'}
                            </Typography>
                          </Box>
                          {backlink.status && (
                            <Chip
                              label={getStatusLabel(backlink.status)}
                              size="small"
                              style={{
                                ...getStatusStyle(backlink.status),
                                fontWeight: 500,
                                fontSize: '9px',
                                height: '16px'
                              }}
                            />
                          )}
                        </Box>
                        
                        {backlink.article_title && (
                          <Typography variant="body2" style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
                            {backlink.article_title}
                          </Typography>
                        )}
                        
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {backlink.article_link && (
                            <Typography 
                              variant="body2" 
                              component="a"
                              href={backlink.article_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#1976d2', 
                                fontSize: '10px', 
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              View Article
                            </Typography>
                          )}
                          {backlink.live_link && (
                            <Typography 
                              variant="body2" 
                              component="a"
                              href={backlink.live_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#1976d2', 
                                fontSize: '10px', 
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              Live Link
                            </Typography>
                          )}
                        </Box>
                        
                        {backlink.target_url && (
                          <Typography variant="body2" style={{ color: '#666', fontSize: '10px', marginTop: '4px', wordBreak: 'break-all' }}>
                            Target: {backlink.target_url}
                          </Typography>
                        )}
                        
                        {backlink.publish_date && (
                          <Typography variant="body2" style={{ color: '#666', fontSize: '10px', marginTop: '2px' }}>
                            Published: {new Date(backlink.publish_date).toLocaleDateString()}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
  );



  if (isFetchingOrders) {
    return (
      <Box pb={2}>
        <div className={classes.card}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            style={{
              textAlign: 'center',
              color: '#666',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
              Loading Orders...
            </Typography>
            <Typography variant="body2" style={{ color: '#999', maxWidth: 400 }}>
              Please wait while we load your premium backlink orders.
            </Typography>
          </Box>
        </div>
      </Box>
    );
  }

  if (!hasOrders) {
    return (
      <Box pb={2}>
        <div className={classes.card}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            style={{
              textAlign: 'center',
              color: '#666',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}
          >
            <Assignment
              style={{
                fontSize: 64,
                color: '#ccc',
                marginBottom: 16
              }}
            />
            <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
              No Orders Yet
            </Typography>
            <Typography variant="body2" style={{ 
              color: '#999', 
              maxWidth: '100%', 
              textAlign: 'center',
              wordWrap: 'break-word',
              lineHeight: '1.4'
            }}>
              When you place your first premium backlink order, it will appear here.
              You&apos;ll be able to track the progress and status of all your orders.
            </Typography>
          </Box>
        </div>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box pb={2}>
        <div className={classes.card}>
          {(() => {
            if (isFetchingOrders) {
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{ color: '#666', height: '200px', padding: '40px 16px' }}
                >
                  <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                    Loading Orders...
                  </Typography>
                  <Typography variant="body2" style={{ color: '#999', maxWidth: 400, textAlign: 'center' }}>
                    Please wait while we load your premium backlink orders.
                  </Typography>
                </Box>
              );
            }
            if (!hasOrders) {
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{ color: '#666', height: '200px', padding: '40px 16px' }}
                >
                  <Assignment
                    style={{
                      fontSize: 64,
                      color: '#ccc',
                      marginBottom: 16
                    }}
                  />
                  <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                    No Orders Yet
                  </Typography>
                  <Typography variant="body2" style={{ color: '#999', maxWidth: 400, textAlign: 'center' }}>
                    When you place your first premium backlink order, it will appear here.
                    You&apos;ll be able to track the progress and status of all your orders.
                  </Typography>
                </Box>
              );
            }
            return (
              <Box>
                {orders.map((order) => renderMobileOrderCard(order))}
              </Box>
            );
          })()}
        </div>
      </Box>
    );
  }

  // Desktop table view
  return (
    <Box pb={2}>
      <div className={classes.card}>
        <div className={classes.tableWrapper}>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} aria-label="orders in process table">
              <TableHead>
                <TableRow>
                  <TableCell width="50px" />
                  <TableCell>Order Summary</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Order Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <React.Fragment key={order.id || `order-${Math.random()}`}>
                    <TableRow style={{ backgroundColor: '#f9f9f9' }}>
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleRowExpand(order.id)}
                        >
                          {expandedRows.has(order.id) ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <div>
                          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                            {order.regular_backlinks?.length || 0} backlinks from {order.order_items?.length || 0} publications
                          </Typography>
                          <Typography variant="body2" style={{ color: '#666', fontSize: '0.875rem' }}>
                            {order.order_items?.map(item => item.backlink_object?.name).filter(Boolean).join(', ') || 'No backlinks'}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        {order.status && (
                          <Chip
                            label={getStatusLabel(order.status)}
                            size="small"
                            style={{
                              ...getStatusStyle(order.status),
                              fontWeight: 500
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>${order.total_cost ? order.total_cost.toLocaleString() : '0'}</TableCell>
                      <TableCell>
                        {(() => {
                          const domains = Array.from(new Set(
                            (order.order_items || [])
                              .map((item) => item.backlink_object?.webmaster_domain)
                              .filter(Boolean),
                          ));
                          return domains.length > 0 ? domains.join(', ') : '-';
                        })()}
                      </TableCell>
                      <TableCell>{order.added ? new Date(order.added).toLocaleDateString() : '-'}</TableCell>
                    </TableRow>
                    <TableRow >
                      <TableCell style={{padding: 0, margin: 0}} colSpan={7}>
                        <Collapse in={expandedRows.has(order.id)} timeout="auto" unmountOnExit>
                          <Box margin="8px 0">
                            {requiresLinkDetails && order.order_items?.some((it) => it.target_url || it.desired_anchor) && (
                              <Box mb={2} p={2} style={{ background: '#F8FBFD', border: '1px solid #E4E7EB', borderRadius: 8 }}>
                                <Typography
                                  style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    color: '#0E7BAF',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.6px',
                                    marginBottom: 8,
                                  }}
                                >
                                  Requested Links
                                </Typography>
                                <Table size="small" aria-label="requested links">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Publication</TableCell>
                                      <TableCell>Target URL</TableCell>
                                      <TableCell>Desired Anchor</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {order.order_items.filter((it) => it.target_url || it.desired_anchor).map((it, idx) => (
                                      <TableRow key={it.id || `oi-${idx}`}>
                                        <TableCell>
                                          {it.backlink_object?.name || it.backlink_object?.webmaster_domain || '-'}
                                        </TableCell>
                                        <TableCell style={{ wordBreak: 'break-all' }}>
                                          {it.target_url || '-'}
                                        </TableCell>
                                        <TableCell>{it.desired_anchor || '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                            <Table size="small" aria-label="backlinks">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Domain</TableCell>
                                  <TableCell align="right">
                                    <Tooltip 
                                      title="Domain Rating (DR), created by Ahrefs, shows the strength of a website's backlink profile on a 100-point scale. Learn more at https://help.ahrefs.com/en/articles/1409408-what-is-domain-rating-dr" 
                                      arrow
                                      classes={{ tooltip: classes.chakraTooltip, arrow: classes.chakraTooltipArrow }}
                                    >
                                      <span style={{ cursor: 'help' }}>DR</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Article Title</TableCell>
                                  <TableCell>Article Link</TableCell>
                                  <TableCell>Live Link</TableCell>
                                  <TableCell>Target URL</TableCell>
                                  <TableCell>Publish Date</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {order.regular_backlinks?.map((backlink, index) => (
                                  <TableRow key={backlink.id || `backlink-${index}`}>
                                    <TableCell component="th" scope="row">
                                      <a 
                                        href={`https://${backlink.webmaster_domain || ''}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className={classes.link}
                                      >
                                        {backlink.webmaster_domain || '-'}
                                      </a>
                                    </TableCell>
                                    <TableCell align="right">
                                      {backlink.dr || '-'}
                                    </TableCell>
                                    <TableCell>
                                      {backlink.status && (
                                        <Chip
                                          label={getStatusLabel(backlink.status)}
                                          size="small"
                                          style={{
                                            ...getStatusStyle(backlink.status),
                                            fontWeight: 500,
                                            fontSize: '0.75rem'
                                          }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {backlink.article_title ? (
                                        <Typography variant="body2" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {backlink.article_title}
                                        </Typography>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                      {backlink.article_link ? (
                                        <a 
                                          href={backlink.article_link} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          className={classes.link}
                                        >
                                          View Article
                                        </a>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                      {backlink.live_link ? (
                                        <a 
                                          href={backlink.live_link} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          className={classes.link}
                                        >
                                          Live Link
                                        </a>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                      {backlink.target_url ? (
                                        <Typography variant="body2" style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {backlink.target_url}
                                        </Typography>
                                      ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                      {backlink.publish_date ? new Date(backlink.publish_date).toLocaleDateString() : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

      </div>
      
    </Box>
  );
};

export default OrdersInProcessTable; 